import React, { useEffect, useState } from "react";
import { CalendarOff, X, Check } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Close off a date range with no guest involved — maintenance, owner use,
 * a road closed by landslide. Writes "Blocked" bookings, the same status
 * availability checks and the outbound iCal export already treat as occupied.
 *
 * Extracted from BookingsPage (where it was local) so the Calendar can use it
 * too. That was the real gap: the month view had no way to block at all, and
 * the list view's only path was a drag gesture — which is undiscoverable, and
 * built on mousedown/mouseenter, so it cannot fire on a touch screen at all.
 * On a phone the timeline just scrolled. A plain button works everywhere.
 *
 * Multi-select rather than the old one-room-or-all dropdown: "block these
 * three for Diwali" is the common case and previously meant repeating the
 * whole flow once per room. The API takes one room per call, so the loop
 * lives here — reported per room, so a partial failure names what failed
 * instead of silently leaving some rooms open.
 */
export default function BlockDatesModal({ onClose, onSaved, authHeaders, initialRoomIds = [] }) {
  const [rooms, setRooms]       = useState([]);
  const [selected, setSelected] = useState(() => new Set(initialRoomIds));
  const [checkIn, setCheckIn]   = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [reason, setReason]     = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    fetch(`${API}/rooms`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => setRooms((j.data || []).filter(r => r.active !== false)))
      .catch(() => {});
  }, [authHeaders]);

  const allSelected = rooms.length > 0 && selected.size === rooms.length;
  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rooms.map(r => r._id)));

  async function submit() {
    if (!checkIn || !checkOut) { setError("Both dates are required."); return; }
    if (selected.size === 0)   { setError("Pick at least one room to block."); return; }
    setSaving(true); setError("");
    try {
      // One call per room type; the backend expands each into its units.
      const results = await Promise.all([...selected].map(roomId =>
        fetch(`${API}/bookings/block`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ roomId, checkIn, checkOut, reason }),
        })
          .then(r => r.json())
          // Carry the created bookings back: BookingsPage prepends them to its
          // list optimistically rather than refetching, so onSaved must hand
          // over an array — calling it bare would blow up its spread.
          .then(j => ({ roomId, ok: !!j.success, error: j.error, created: j.data || [] }))
      ));

      const failed = results.filter(r => !r.ok);
      if (failed.length) {
        // Name the rooms that failed — "some rooms are still open" is the
        // dangerous outcome here, so it must never be silent.
        const names = failed.map(f => rooms.find(r => r._id === f.roomId)?.name || "a room");
        throw new Error(`Could not block ${names.join(", ")}: ${failed[0].error || "unknown error"}`);
      }
      onSaved?.(results.flatMap(r => r.created));
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10
        w-full max-w-sm max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <CalendarOff size={16} className="text-gray-500" /> Block Dates
          </h3>
          <button onClick={onClose} aria-label="Close" className="tap-44">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-500">Rooms *</label>
              <button type="button" onClick={toggleAll}
                className="text-[11px] font-medium text-amber-600 hover:text-amber-700">
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-44 overflow-y-auto bg-white">
              {rooms.length === 0 && <p className="text-xs text-gray-400 px-3 py-3">No rooms yet.</p>}
              {rooms.map(r => {
                const on = selected.has(r._id);
                return (
                  <button key={r._id} type="button" onClick={() => toggle(r._id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 min-h-[44px] text-left hover:bg-amber-50/60 transition-colors">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                      ${on ? "bg-amber-500 border-amber-500" : "border-gray-300 bg-white"}`}>
                      {on && <Check size={11} className="text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-gray-800 truncate">{r.name}</span>
                      <span className="block text-[11px] text-gray-400">
                        {(r.units || []).filter(u => u.active !== false).length || 1} room
                        {((r.units || []).filter(u => u.active !== false).length || 1) !== 1 ? "s" : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {selected.size > 0 && (
              <p className="text-[11px] text-gray-400 mt-1">
                {selected.size} room type{selected.size !== 1 ? "s" : ""} selected — every room of each will be blocked.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">From *</label>
              <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">To *</label>
              <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Maintenance, Owner use"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>

          {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 min-h-[44px] rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="flex-1 min-h-[44px] rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Blocking…" : "Block dates"}
          </button>
        </div>
      </div>
    </div>
  );
}
